import { useState, useCallback } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { fetchAddSiteImage, fetchSiteImages } from '@/features/sites/sitesThunks';
import { SiteImages } from '@/types/site.types';
import { UseSnackbarReturn } from '@/hooks/useSnackbar';

interface UseImageUploadParams {
    selectedSite: string | null;
    authUserId: string | undefined;
    snackbar: UseSnackbarReturn;
}

interface UseImageUploadReturn {
    uploadingSiteId: string | null;
    isUploading: boolean;
    handleAddPhotos: (siteName: string) => void;
}

/**
 * Custom hook to handle image upload flow
 * @param params - Configuration including selected site, auth user, and snackbar
 * @returns Upload state and handler function
 */
export const useImageUpload = ({ 
    selectedSite, 
    authUserId, 
    snackbar 
}: UseImageUploadParams): UseImageUploadReturn => {
    const dispatch = useAppDispatch();
    const [uploadingSiteId, setUploadingSiteId] = useState<string | null>(null);

    const handleAddPhotos = useCallback((siteName: string) => {
        setUploadingSiteId(siteName);
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = false;

        input.onchange = async (e: Event) => {
            const target = e.target as HTMLInputElement;
            const file = target.files?.[0];

            if (file && authUserId) {
                try {
                    const payload: SiteImages = {
                        site: siteName,
                        section: 'Sedes',
                        imageUrl: file.name,
                        user: authUserId
                    };

                    await dispatch(fetchAddSiteImage({ imageData: payload, file })).unwrap();
                    dispatch(fetchSiteImages(selectedSite));
                    snackbar.show('Foto agregada exitosamente', 'success');
                } catch (error) {
                    snackbar.show(`Error al subir la foto. (${error}) Intenta nuevamente.`, 'error');
                } finally {
                    setUploadingSiteId(null);
                    input.value = '';
                }
            } else {
                setUploadingSiteId(null);
            }
        };

        // Handle when user closes the dialog without selecting a file
        input.oncancel = () => {
            setUploadingSiteId(null);
        };

        input.click();
    }, [dispatch, selectedSite, authUserId, snackbar]);

    return {
        uploadingSiteId,
        isUploading: uploadingSiteId !== null,
        handleAddPhotos
    };
};
